<template>
  <div class="space-y-4 sm:space-y-6">
    <!-- Welcome Section -->
    <div class="card mb-6">
      <div class="card-body">
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6"
        >
          <div
            class="h-16 w-16 sm:h-20 sm:w-20 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-white text-2xl sm:text-3xl font-semibold shadow-lg transform transition-transform duration-300 hover:scale-105"
          >
            <img
              v-if="userImage"
              :src="userImage"
              :alt="userName"
              class="w-full h-full object-cover"
            />
            <span v-else>{{ userInitials }}</span>
          </div>
          <div class="text-center sm:text-left">
            <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Welcome, {{ userName }}!
            </h1>
            <p class="text-sm sm:text-base text-secondary-text dark:text-secondary-dark-text mt-1">
              {{ userEmail }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Register employee Modal -->
  <RegisterEmployeeModal
    :is-visible="isRegisterModalVisible"
    @close="isRegisterModalVisible = false"
    @success="handleRegistrationSuccess"
  />

  <!-- Edit employee Modal -->
  <EditEmployeeModal
    :is-visible="isEditModalVisible"
    :employee="selectedEmployee"
    @close="isEditModalVisible = false"
    @success="handleEditSuccess"
  />

  <!-- Delete employee Modal -->
  <DeleteEmployeeModal
    :is-visible="isDeleteModalVisible"
    :employee="selectedEmployeeForDelete"
    @close="isDeleteModalVisible = false"
    @success="handleDeleteSuccess"
  />

  <!-- View employee Details Modal -->
  <ViewEmployeeDetails
    :is-visible="isViewDetailsModalVisible"
    :employee="selectedEmployeeForView || undefined"
    @close="handleViewDetailsClose"
  />

  <!-- Loading State -->
  <div v-if="isLoading" class="mt-6 flex flex-col justify-center items-center py-12">
    <vue-loaders
      name="line-scale"
      :color="isDarkMode ? '#818cf8' : '#4f46e5'"
      scale="1.2"
      class="mb-4"
    />
    <span class="text-gray-900 dark:text-white">Loading employees...</span>
  </div>

  <!-- Error State -->
  <div
    v-else-if="error"
    class="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
  >
    <div class="flex items-center">
      <AlertCircle class="h-5 w-5 text-red-500 mr-2" />
      <span class="text-red-700 dark:text-red-300 font-medium">{{ error }}</span>
    </div>
    <button
      @click="fetchEmployees"
      class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
    >
      Try again
    </button>
  </div>

  <!-- Main Content -->
  <div v-else class="space-y-4 sm:space-y-6">
    <!-- employee Data Table -->
    <DataTable
      :data="employees"
      :columns="tableColumns"
      :loading="isLoading"
      :sortable-columns="sortableColumns"
      :mobile-columns="mobileColumns"
      item-key="user_details_id"
      item-label="employees"
      search-placeholder="Search by name, email..."
      :summary-icon="Users"
      :show-search="true"
      :show-filters="true"
      :show-pagination="true"
      :show-summary="true"
      :items-per-page="10"
      :items-per-page-options="[5, 10, 25, 50]"
      table-id="employee-table"
      @search="handleSearch"
      @sort="handleSort"
      @filter="handleFilter"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    >
      <!-- Custom action buttons -->
      <template #actions>
        <button @click="registerNewUser" class="btn btn-primary btn-sm flex items-center gap-2">
          <Plus class="h-4 w-4" />
          <span class="hidden sm:inline">Register New</span>
        </button>
      </template>

      <!-- Custom filters -->
      <template #filters="{ updateFilter }">
        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            v-model="statusFilter"
            @change="updateFilter('status', statusFilter)"
            class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </template>

      <!-- Custom summary -->
      <template #summary="{ totalItems }">
        <div v-if="totalItems > 0" class="flex items-center gap-4 text-sm">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 bg-green-500 rounded-full"></div>
            <span class="text-gray-600 dark:text-gray-400">
              <span class="font-medium text-green-600 dark:text-green-400">{{
                activeEmployees
              }}</span>
              active
            </span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 bg-red-500 rounded-full"></div>
            <span class="text-gray-600 dark:text-gray-400">
              <span class="font-medium text-red-600 dark:text-red-400">{{
                inactiveEmployees
              }}</span>
              inactive
            </span>
          </div>
        </div>
      </template>

      <!-- Email column cell -->
      <template #cell-email="{ value }">
        <span class="break-all">{{ value }}</span>
      </template>

      <template #mobile-email="{ value }">
        <span class="break-all">{{ value }}</span>
      </template>

      <!-- Middle Name column cell -->
      <template #cell-middle_name="{ value }">
        <span :class="value ? '' : 'text-gray-400 dark:text-gray-500 italic'">
          {{ value || 'Not stated' }}
        </span>
      </template>

      <template #mobile-middle_name="{ value }">
        <span :class="value ? '' : 'text-gray-400 dark:text-gray-500 italic'">
          {{ value || 'Not stated' }}
        </span>
      </template>

      <!-- Mobile Status column cell -->
      <template #mobile-status="{ item }">
        <span
          :class="[
            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
            item.status === 'Active'
              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
              : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
          ]"
        >
          {{ item.status }}
        </span>
      </template>

      <!-- Profile column cell -->
      <template #cell-profile="{ item }">
        <div class="flex items-center">
          <div
            class="h-10 w-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
          >
            <img
              v-if="item.user_image"
              :src="item.user_image"
              :alt="`${item.first_name} ${item.last_name}`"
              class="h-full w-full object-cover"
              @error="handleImageError"
            />
            <span v-else class="text-gray-500 text-sm">
              {{ item.first_name?.charAt(0) || '' }}{{ item.last_name?.charAt(0) || '' }}
            </span>
          </div>
        </div>
      </template>

      <!-- Status column cell -->
      <template #cell-status="{ item }">
        <div class="flex justify-center">
          <span
            :class="[
              'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
              item.status === 'Active'
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            ]"
          >
            {{ item.status }}
          </span>
        </div>
      </template>

      <!-- Actions column -->
      <template #row-actions="{ item }">
        <div class="flex justify-center space-x-3">
          <!-- Status Toggle Icon Button -->
          <button
            :class="[
              'transition duration-200 transform',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark dark:hover:text-gray-500 hover:text-gray-900'
                : 'text-primary dark:text-primary-dark-text hover:text-gray-900 dark:hover:text-gray-500'
            ]"
            @click="toggleUserStatus(item)"
            :title="item.status === 'Active' ? 'Deactivate User' : 'Activate User'"
          >
            <LockOpen v-if="item.status === 'Active'" class="h-5 w-5" />
            <Lock v-else class="h-5 w-5" />
          </button>

          <!-- View Icon Button -->
          <button
            class="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition"
            @click="viewEmployee(item)"
            title="View User"
          >
            <Eye class="h-5 w-5" />
          </button>

          <!-- Edit Icon Button -->
          <button
            class="text-blue-500 hover:text-blue-700 transition"
            @click="editEmployee(item)"
            title="Edit User"
          >
            <Edit3 class="h-5 w-5" />
          </button>

          <!-- Delete Icon Button -->
          <button
            class="text-red-500 hover:text-red-700 transition"
            @click="deleteUser(item)"
            title="Delete User"
          >
            <Trash2 class="h-5 w-5" />
          </button>
        </div>
      </template>

      <!-- Mobile actions -->
      <template #mobile-actions="{ item }">
        <div class="flex justify-end space-x-3">
          <!-- Status Toggle -->
          <button
            :class="[
              'transition duration-200 transform flex items-center gap-1 px-2 py-1 rounded text-sm',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
            @click="toggleUserStatus(item)"
            :title="item.status === 'Active' ? 'Deactivate User' : 'Activate User'"
          >
            <LockOpen v-if="item.status === 'Active'" class="h-4 w-4" />
            <Lock v-else class="h-4 w-4" />
            <span class="hidden sm:inline">
              {{ item.status === 'Active' ? 'Deactivate' : 'Activate' }}
            </span>
          </button>

          <!-- View Button -->
          <button
            class="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="viewEmployee(item)"
            title="View employee"
          >
            <Eye class="h-4 w-4" />
            <span class="hidden sm:inline">View</span>
          </button>

          <!-- Edit Button -->
          <button
            class="text-blue-500 hover:text-blue-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
            @click="editEmployee(item)"
            title="Edit employee"
          >
            <Edit3 class="h-4 w-4" />
            <span class="hidden sm:inline">Edit</span>
          </button>

          <!-- Delete Button -->
          <button
            class="text-red-500 hover:text-red-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
            @click="deleteUser(item)"
            title="Delete employee"
          >
            <Trash2 class="h-4 w-4" />
            <span class="hidden sm:inline">Delete</span>
          </button>
        </div>
      </template>

      <!-- Empty actions -->
      <template #empty-actions>
        <button @click="registerNewUser" class="btn btn-primary btn-sm">Register</button>
      </template>
    </DataTable>
  </div>
  <!-- End of main content wrapper -->
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useEmployees } from '@/composables/employee/useEmployees'
import { useEmployeeStore } from '@/stores/employee'
import { useThemeStore } from '@/stores/theme'
import { employeeService } from '@/services/user'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import RegisterEmployeeModal from '@/components/modals/RegisterEmployee.vue'
import EditEmployeeModal from '@/components/modals/EditEmployee.vue'
import DeleteEmployeeModal from '@/components/modals/DeleteEmployee.vue'
import ViewEmployeeDetails from '@/components/modals/ViewEmployeeDetails.vue'
import DataTable from '@/components/common/DataTable.vue'
import { 
  AlertCircle,
  Plus,
  Lock,
  LockOpen,
  Eye,
  Edit3,
  Trash2,
  Users
} from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
useTitle(`Employees - ${appTitle.value}`)

const { userInitials, userName, userEmail, userImage } = useUser()

// Toast service for notifications
const toast = useToastService()

// Theme store for dark mode detection
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// employee management
const {
  employees,
  isLoading,
  error,
  fetchEmployees,
  setSearch,
  setStatusFilter,
  setPagination,
  activeEmployees,
  inactiveEmployees,
  statusFilter: storedStatusFilter
} = useEmployees()

// Modal state
const isRegisterModalVisible = ref(false)
const isEditModalVisible = ref(false)
const isDeleteModalVisible = ref(false)
const isViewDetailsModalVisible = ref(false)
const selectedEmployee = ref<employee | null>(null)
const selectedEmployeeForDelete = ref<employee | null>(null)
const selectedEmployeeForView = ref<employee | null>(null)

// Local filter state - initialize from stored value
const statusFilter = ref(storedStatusFilter.value)

// Table configuration
const tableColumns = [
  { key: 'profile', label: 'Profile', sortable: false, width: '90px' },
  { key: 'first_name', label: 'First Name', sortable: true },
  { key: 'last_name', label: 'Last Name', sortable: true },
  { key: 'middle_name', label: 'Middle Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'status', label: 'Status', sortable: true, align: 'center' as const, width: '90px' }
]

const sortableColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' }
]

const mobileColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'middle_name', label: 'Middle Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' }
]

// DataTable event handlers
const handleSearch = (query: string) => {
  setSearch(query)
}

const employeeStore = useEmployeeStore()

const handleSort = (sort: { field: string; direction: 'asc' | 'desc' }) => {
  employeeStore.setFilters({ sortBy: sort.field, sortDirection: sort.direction })
}

const handleFilter = (filters: Record<string, any>) => {
  if (filters.status !== undefined) {
    statusFilter.value = filters.status
    setStatusFilter(filters.status as 'Active' | 'Inactive' | '')
  }
}

const handlePageChange = (page: number) => {
  // The DataTable component handles pagination internally
  console.log('Page change:', page)
}

const handleItemsPerPageChange = (itemsPerPage: number) => {
  setPagination({ itemsPerPage })
}

// Helper functions
const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  if (target) {
    target.style.display = 'none'
  }
}

// Modal handlers
const handleRegistrationSuccess = () => {
  fetchEmployees()
}

const handleEditSuccess = () => {
  fetchEmployees()
}

const handleDeleteSuccess = () => {
  // Clear search filter to show all employees after deletion
  setSearch('')
  fetchEmployees()
}

// employee action functions
const registerNewUser = () => {
  isRegisterModalVisible.value = true
}

const toggleUserStatus = async (user: any) => {
  try {
    const newStatus = user.status === 'Active' ? 0 : 1
    const action = newStatus === 1 ? 'activated' : 'deactivated'

    await employeeService.updateUserStatus(user.user_details_id, newStatus)

    // Update the user status locally without refetching all data
    user.status = newStatus === 1 ? 'Active' : 'Inactive'

    toast.success(`${user.first_name} ${user.last_name} has been ${action}.`)
  } catch (error: any) {
    console.error('Error toggling user status:', error)
    toast.error('Failed to update user status. Please try again.')
  }
}

const viewEmployee = (user: any) => {
  selectedEmployeeForView.value = user
  isViewDetailsModalVisible.value = true
}

const handleViewDetailsClose = () => {
  isViewDetailsModalVisible.value = false
  selectedEmployeeForView.value = null
}

const editEmployee = (user: any) => {
  selectedEmployee.value = user
  isEditModalVisible.value = true
}

const deleteUser = (user: employee) => {
  selectedEmployeeForDelete.value = user
  isDeleteModalVisible.value = true
}

// Load employees on component mount
onMounted(async () => {
  await fetchEmployees()
})
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

/* Custom focus styles for better accessibility */
.focus\:ring-primary:focus {
  --tw-ring-opacity: 0.5;
  --tw-ring-color: rgb(79 70 229 / var(--tw-ring-opacity));
}

/* Hover effects for interactive elements */
input:hover,
select:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Dark mode ring colors */
.dark .focus\:ring-primary:focus {
  --tw-ring-color: rgb(129 140 248 / var(--tw-ring-opacity));
}
</style>
